import { useEffect, useState } from "react";
import Section from "../components/Section";
import "./InvestorRelations.css";

const GNIM_ADDRESS = "0xc12E912Df40b585fb2F436588b7F28179Ff8A33D";
const SUPPLY_ENDPOINT = "https://api.impactgena2.workers.dev/gnim/supply";
const ETHERSCAN_URL = `https://etherscan.io/address/${GNIM_ADDRESS}`;

const formatSupply = (value) => {
  if (!value) return "";
  const [whole, fraction] = value.split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (!fraction) return withCommas;
  const trimmed = fraction.replace(/0+$/, "");
  return trimmed ? `${withCommas}.${trimmed}` : withCommas;
};

const InvestorRelations = () => {
  const [status, setStatus] = useState("loading");
  const [supply, setSupply] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;

    const loadSupply = async () => {
      try {
        const res = await fetch(SUPPLY_ENDPOINT, { method: "GET" });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        if (!active) return;
        setSupply(formatSupply(data.totalSupply));
        setUpdatedAt(data.updatedAt || "");
        setStatus("success");
      } catch {
        if (!active) return;
        setStatus("error");
      }
    };

    loadSupply();
    return () => {
      active = false;
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(GNIM_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const supplyValue =
    status === "success" ? supply : status === "error" ? "Unavailable" : "Loading...";
  const updatedValue =
    status === "success" && updatedAt
      ? new Date(updatedAt).toLocaleString()
      : status === "error"
        ? "Unavailable"
        : "Loading...";

  return (
    <main className="ir-page">
      <section className="ir-hero">
        <div className="container">
          <div className="ir-eyebrow">Investor Relations</div>
          <h1>Investor Relations</h1>
        </div>
      </section>

      <Section id="ir-overview" title="Overview" className="ir-section">
        <p>
          GenaImpact is a product and engineering studio focused on building real-world software in
          Web3 and AI. This page provides high-level information for parties seeking to understand
          GNIM and potential OTC discussions.
        </p>
      </Section>

      <Section id="ir-token-basics" title="Token basics" className="ir-section">
        <div className="ir-token-grid">
          <div className="ir-list">
            <div className="ir-list-row">
              <span className="ir-label">Token</span>
              <span>GNIM</span>
            </div>
            <div className="ir-list-row">
              <span className="ir-label">Network</span>
              <span>Ethereum (ERC-20)</span>
            </div>
            <div className="ir-list-row">
              <span className="ir-label">Contract</span>
              <span className="mono">{GNIM_ADDRESS}</span>
            </div>
            <div className="ir-list-row">
              <span className="ir-label">On-chain total supply</span>
              <span>(shown below)</span>
            </div>
            <div className="ir-list-row">
              <span className="ir-label">Explorer</span>
              <a href={ETHERSCAN_URL} target="_blank" rel="noreferrer">
                Etherscan
              </a>
            </div>
          </div>

          <div className="card ir-card">
            <h3>Contract &amp; Supply (Verifiable On-Chain)</h3>
            <div className="ir-rows">
              <div className="ir-row">
                <span className="ir-label">Contract address</span>
                <div className="ir-row-value">
                  <span className="mono">{GNIM_ADDRESS}</span>
                  <button type="button" className="ir-copy" onClick={handleCopy}>
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="ir-row">
                <span className="ir-label">Total supply</span>
                <div className="ir-row-value">
                  <span>{supplyValue}</span>
                </div>
              </div>
              <div className="ir-row">
                <span className="ir-label">Last updated</span>
                <div className="ir-row-value">
                  <span>{updatedValue}</span>
                </div>
              </div>
            </div>

            {status === "error" ? (
              <p className="ir-muted">
                Supply data unavailable. Verify on{" "}
                <a href={ETHERSCAN_URL} target="_blank" rel="noreferrer">
                  Etherscan
                </a>
                .
              </p>
            ) : null}

            <div className="ir-notes">
              <p>Always verify the contract address via official GenaImpact channels.</p>
              <p>Do not send ETH or tokens to the token contract address.</p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="ir-use-cases" title="Use cases" className="ir-section">
        <p>
          GNIM is intended to support ecosystem participation and alignment. Potential use cases
          may include platform utility (where applicable), ecosystem incentives (where compliant),
          and governance signaling (future, if implemented). Utilities may evolve as products
          mature and compliance requirements are assessed.
        </p>
      </Section>

      <Section id="ir-risk" title="Risk &amp; disclosures" className="ir-section">
        <p>
          Digital assets involve material risks, including volatility, liquidity constraints, smart
          contract risk, regulatory risk, and operational risk. This page is for informational
          purposes only and does not constitute an offer, solicitation, or recommendation in any
          jurisdiction. Any potential transaction is subject to eligibility and compliance review.
        </p>
      </Section>

      <Section id="ir-contact" title="Contact" className="ir-section">
        <p>
          Investor inquiries:{" "}
          <a href="mailto:investor@genaimpact.com">investor@genaimpact.com</a>
        </p>
        <p>
          General inquiries:{" "}
          <a href="mailto:contact@genaimpact.com">contact@genaimpact.com</a>
        </p>
      </Section>
    </main>
  );
};

export default InvestorRelations;
